import React from 'react';
import { styled } from 'linaria/react';
import { theme } from '../../Theme';
import { PaginatedSlider } from './PaginatedSlider';
import MaxWidth from '../../Layout/MaxWidth';
import { LinkEvaluator } from '../../../utils/LinkEvaluator';
import { css, cx } from 'linaria';
import Image from '@jetshop/ui/Image/Image';
import { textShadowStyle } from '../BoxItems';

const OuterWrapper = styled(MaxWidth)`
  --horizontal-mobile-padding: 10px;
  --vertical-mobile-padding: 40px;
  --horizontal-desktop-padding: 32px;
  --vertical-desktop-padding: 40px;

  padding: var(--horizontal-mobile-padding);

  &.vertial-padding {
    /* Keep left/right padding symmetric so the slider aligns with other components */
    padding: var(--vertical-desktop-padding) var(--horizontal-desktop-padding);

    ${theme.below.md} {
      padding: var(--vertical-mobile-padding) var(--horizontal-mobile-padding);
    }
  }

  h2 {
    text-align: center;
    font-family: Lato;
    font-size: 30px;
    font-style: normal;
    font-weight: 600;
    line-height: normal;
    margin: 0px 0 24px 0;
    color: ${(props) => props.titlecolor};
  }

  .title-html {
    width: fit-content;
    margin: 0px auto 24px auto;

    > h2 {
      color: inherit;
    }
  }

  .link-evaluator {
    width: fit-content;
    margin: 24px auto 0 auto;
    text-decoration: none;
  }
`;

export const SliderCTA = styled('div')`
  width: fit-content;
  margin: 0 auto;
  font-size: 14px;
  font-style: normal;
  text-decoration: none;
  padding: 7px 20px;
  border-radius: 20px;
  text-decoration: none;
  text-transform: uppercase;
  color: ${(props) => props.buttontextcolor};
  background: ${(props) => props.buttonbackgroundcolor};
  font-weight: ${(props) => props.fontweight ?? '700'};

  &:hover {
    filter: brightness(1.1);
  }
`;

export const StartPageCardSlider = ({
  backgroundColor,
  title,
  titleHtml,
  titleColor,
  buttonText,
  buttonTextColor,
  buttonBackgroundColor,
  buttonTextFontWeight,
  link,
  children,
}) => {
  const showButton = buttonText?.value && link?.value;
  return (
    <div
      className="startpagecardslider-component-wrapper"
      style={{
        backgroundColor: backgroundColor?.value || undefined,
      }}
    >
      <OuterWrapper
        className={
          backgroundColor?.value &&
          backgroundColor?.value?.toLowerCase() !== '#ffffff' &&
          'vertial-padding'
        }
        titlecolor={titleColor?.value ?? '#000'}
      >
        {title?.value && <h2>{title?.value}</h2>}
        {titleHtml?.value?.length > 0 && (
          <div
            className="title-html"
            dangerouslySetInnerHTML={{ __html: titleHtml?.value }}
          />
        )}

        <PaginatedSlider backgroundColor={backgroundColor?.value}>
          {children}
        </PaginatedSlider>
        {showButton && (
          <LinkEvaluator link={link}>
            <SliderCTA
              buttontextcolor={buttonTextColor?.value ?? '#fff'}
              buttonbackgroundcolor={buttonBackgroundColor?.value ?? '#000'}
              fontweight={buttonTextFontWeight?.value ?? '700'}
            >
              {buttonText?.value}
            </SliderCTA>
          </LinkEvaluator>
        )}
      </OuterWrapper>
    </div>
  );
};

const linkEvaluatorStyle = css`
  .link-evaluator {
    color: inherit;
  }
  .no-link {
    display: inline;
  }

  .below-image-text {
    margin: 10px 0 0 0;
    font-size: 14px;
    font-style: normal;
    font-weight: 400;
    line-height: 150%;
    color: inherit;
  }
`;

const SliderItemWrapper = styled('div')`
  position: relative;

  .no-link {
    display: inline;
  }

  [data-flight-image-container] {
    border-radius: 10px;
  }

  .content-wrapper {
    position: absolute;
    top: 0;
    left: 0;
    bottom: 0;
    right: 0;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    padding: 20px 20px 20px 20px;

    .vertical-align-helper {
      height: 40px;
      width: 100%;
      background: transparent;
    }
  }

  .html-on-image {
    > * {
      line-height: normal;
      margin: 0;
    }
    /* Add margin between heading and the following paragraph */
    > h1 + p,
    > h2 + p,
    > h3 + p,
    > h4 + p,
    > h5 + p,
    > h6 + p {
      margin-top: 16px;
    }
  }
`;

const titleOnImageStyle = css`
  font-size: 26px;
  font-weight: 600;
  line-height: 1.2;
  text-align: center;
  letter-spacing: 1.3px;
  margin: 0;
`;

export const TitleOnImage = ({ text, textColor, shadow }) => {
  if (!text) {
    return null;
  }

  return (
    <h2
      className={cx(
        'title-on-image',
        titleOnImageStyle,
        textShadowStyle,
        `shadow-${shadow?.value}`
      )}
      style={{ color: textColor?.value || textColor || '#fff' }}
    >
      {text?.value || text}
    </h2>
  );
};

export const HtmlOnImage = ({ html, shadow }) => {
  if (!html?.value) {
    return null;
  }

  return (
    <div
      className={cx(
        'html-on-image',
        textShadowStyle,
        `shadow-${shadow?.value}`
      )}
      dangerouslySetInnerHTML={{ __html: html?.value }}
    />
  );
};
export const StartPageCardSliderItem = ({
  image,
  html,
  onImageTextShadow,
  belowImageText,
  buttonText,
  buttonTextFontWeight,
  buttonTextColor,
  buttonBackgroundColor,
  link,
  belowImageTextColor,
}) => {
  return (
    <div className={linkEvaluatorStyle} style={{ marginBottom: '20px' }}>
      <LinkEvaluator link={link}>
        <SliderItemWrapper>
          <Image
            sizes={[1, 1 / 2, 1 / 3, 1 / 4]}
            alt={''}
            src={image?.value}
            cover={true}
            aspect={'255:320'}
          />
          <div
            className={cx('content-wrapper', !buttonText?.value && 'center')}
          >
            <div className="vertical-align-helper" />
            {html?.value && (
              <HtmlOnImage html={html} shadow={onImageTextShadow} />
            )}

            {buttonText?.value ? (
              <SliderCTA
                buttontextcolor={buttonTextColor?.value ?? '#fff'}
                buttonbackgroundcolor={buttonBackgroundColor?.value ?? '#000'}
                fontweight={buttonTextFontWeight?.value ?? '700'}
              >
                {buttonText?.value}
              </SliderCTA>
            ) : (
              <div className="vertical-align-helper" />
            )}
          </div>
        </SliderItemWrapper>
        {belowImageText?.value && (
          <div
            className="below-image-text"
            style={{ color: belowImageTextColor?.value ?? '#fff' }}
            dangerouslySetInnerHTML={{ __html: belowImageText?.value }}
          />
        )}
      </LinkEvaluator>
    </div>
  );
};
